/** Content + plan config for the feature-aware paywall (#131).
 * Plan preselection comes from live feature->plan upgrade data (2026-07-12):
 * schedule 139/183 Basic; recurring is the top PQL signal (Basic);
 * campaigns over-index Standard (8/27); gateway appears only in Pro (3/54); api 2x Pro rate.
 * Prices are the live Chargebee list prices of 2026-07-12 (display only —
 * checkout always charges the live price; live-price fetch is a follow-up). */
export type PaywallFeature = 'scheduling' | 'recurring' | 'campaigns' | 'gateway' | 'api';

export interface PaywallVariant {
  feature: PaywallFeature;
  plan: 'Basic' | 'StandardNew' | 'Pro';
  monthly: number;        // $/mo, monthly billing
  yearlyPerMonth: number; // $/mo, yearly billing
  yearlyTotal: number;    // $ billed yearly
  savePct: number;        // real per-plan annual discount
  i18nPrefix: string;     // paywall.<feature>
  eyebrow: string;
  title: string;
  sub: string;
  keep?: string;          // draft-safety reassurance line
  bullets: [string, string, string];
  cta: string;            // without price suffix; UI appends " · $N/mo"
}

export const itemPriceId = (plan: PaywallVariant['plan'], cycle: 'Yearly' | 'Monthly'): string =>
  `${plan}-USD-${cycle}`;

/** Option-3 (past-fatigue-cap) content — a one-time no-card 7-day Pro trial offer
 * (backend action `start-pro-trial`), and the quiet non-blocking hint shown afterward
 * if the offer is declined. See `chooseWallState` in `@common/paywall/wall-exposure`. */
export const SOFT_SCHEDULING_OFFER = {
  eyebrow: 'You schedule a lot',
  title: 'Try Pro free for 7 days',
  sub: 'You keep bumping the free limit — here are 7 days of unlimited scheduling on us. No card, cancels itself when the week is up.',
  primaryCta: 'Start my 7-day trial',
  secondaryCta: 'Not now',
};
export const SCHEDULING_HINT = 'Free lines up 3 at a time — upgrade anytime for unlimited.';

const BASIC = { plan: 'Basic' as const, monthly: 9, yearlyPerMonth: 7, yearlyTotal: 84, savePct: 22 };
const STANDARD = { plan: 'StandardNew' as const, monthly: 25, yearlyPerMonth: 20, yearlyTotal: 240, savePct: 20 };
const PRO = { plan: 'Pro' as const, monthly: 50, yearlyPerMonth: 45, yearlyTotal: 540, savePct: 10 };

export const PAYWALL_VARIANTS: Record<PaywallFeature, PaywallVariant> = {
  scheduling: {
    feature: 'scheduling', ...BASIC, i18nPrefix: 'paywall.scheduling',
    eyebrow: 'Unlock unlimited scheduling',
    title: 'Schedule as many as you want',
    sub: 'Free lets you line up 3 at once — Basic removes the cap so every message and campaign can wait in the queue, as far ahead as you like.',
    keep: 'Your draft is saved — nothing you wrote is lost.',
    bullets: [
      'Unlimited scheduled messages',
      'Recurring sends — daily, weekly, monthly',
      'Templates & smart reminders',
    ],
    cta: 'Unlock scheduling',
  },
  recurring: {
    feature: 'recurring', ...BASIC, i18nPrefix: 'paywall.recurring',
    eyebrow: 'Basic feature · Recurring',
    title: 'Write it once, send it every week',
    sub: 'Recurring schedules are a Basic feature. Set the rhythm — Blueticks keeps sending, even when you forget.',
    bullets: [
      'Daily, weekly, monthly — any rule',
      'Ends automatically when you say so',
      'Unlimited one-time scheduling included',
    ],
    cta: 'Unlock recurring',
  },
  campaigns: {
    feature: 'campaigns', ...STANDARD, i18nPrefix: 'paywall.campaigns',
    eyebrow: 'Free plan · 1-message campaign',
    title: 'Send the whole campaign',
    sub: 'Free sends a single-message campaign with Blueticks branding. Standard sends the full sequence to your whole audience — unbranded.',
    keep: 'Your audience and messages stay exactly as you built them.',
    bullets: [
      'Multi-message campaigns & drip sequences',
      'Stop-on-reply — never pitch someone mid-conversation',
      'Delivery & reply analytics per campaign',
    ],
    cta: 'Unlock full campaigns',
  },
  gateway: {
    feature: 'gateway', ...PRO, i18nPrefix: 'paywall.gateway',
    eyebrow: 'Pro feature · Always-on',
    title: 'Send while your computer sleeps',
    sub: 'The always-on cloud gateway keeps your WhatsApp connected and sending — no browser open, no laptop on, no missed schedule.',
    bullets: [
      'Always-on sending from the cloud',
      'Overnight & weekend schedules just fire',
      'Everything in Standard included',
    ],
    cta: 'Turn on always-on sending',
  },
  api: {
    feature: 'api', ...PRO, i18nPrefix: 'paywall.api',
    eyebrow: 'Pro feature · API + MCP',
    title: 'Put WhatsApp in your stack',
    sub: 'Pro unlocks the /v1 developer API and the MCP server — schedule, send, and run campaigns from code, or hand the keys to your AI agent.',
    bullets: [
      'REST API with webhooks & idempotency',
      'MCP server — works with Claude & any agent',
      'Always-on cloud gateway included',
    ],
    cta: 'Unlock the API',
  },
};
